<template>
	<div v-if="!isLoading">
		<div class="main">
			<form v-if="payment" ref="paymentForm">
				<div class="font-bold text-left text-xl mb-4">{{ $t('statistical_data') }}</div>

				<div class="w-full md:flex md:space-x-4">
					<div class="w-full flex flex-col">
						<div class="w-full flex gap-4">
							<BaseDatepicker
								ref="Rechnungsdatum"
								:label="$t('billing_date')"
								:placeholder="$t('billing_date')"
								class="w-1/3"
								v-model="payment.date"
								error-behavior="blur"
								@input="changeDate"
								:disabled="isUserAllowedInLevel3 || isClosed"
								:required="true"
							/>
							<BaseDatepicker label="Zahlungsziel" class="w-1/3" v-model="payment.zahlungsziel" :disabled="isUserAllowedInLevel3 || isClosed" />
							<BaseInputNumber
								class="w-1/3"
								ref="rechnungsbetrag"
								:label="$t('billing_value')"
								:sublabel="$t('gross_braces')"
								v-model="payment.rechnungsbetrag"
								:required="true"
								:precision="2"
								:disabled="isUserAllowedInLevel3 || isClosed"
							/>
						</div>
					</div>
				</div>

				<div class="mt-8 flex flex-col xl:flex-row" v-if="payment.rechnungsbetrag > 0">
					<div class="xl:w-3/4">
						<div class="flex w-full">
							<div class="flex justify-between mb-4 flex-grow">
								<div class="font-bold text-left text-xl">{{ $t('payment_transactions') }}</div>
								<div>
									<BaseButton @click="addPaymentReceipt" class="w-auto min-w-0" v-if="!isUserAllowedInLevel3 && !isClosed">
										<font-awesome-icon :icon="['fas', 'plus']" class="text-gray-700 sm:mr-2" />
										<label class="hidden sm:inline">{{ $t('add') }}</label>
									</BaseButton>
								</div>
							</div>
						</div>

						<div v-for="receipt in payment.zahlungen" :key="receipt.id" class="pb-2">
							<DamageCasePaymentReceiptRow
								:ref="'DamageCasePaymentReceiptRow.' + receipt.id"
								:receipt="receipt"
								:payer-options="payerOptions"
								:type-options="typeOptions"
								:overpayment-options="overpaymentSelectOptions"
								:has-one-overpayment="payment.zahlungen.some(element => element.overpaymentType)"
								:is-overpayment="checkIsOverpayment(receipt)"
								:not-payed="notPayed"
								:disabled="isUserAllowedInLevel3 || isClosed"
								@change="checkOverPay"
							/>
						</div>
					</div>

					<div class="mt-4 xl:mt-0 xl:mx-16 text-left xl:w-1/4">
						<div class="font-bold text-left text-xl mb-4 xl:mb-8">{{ $t('billing_details') }}</div>
						<div class="flex justify-between">
							<div>{{ $t('gross_sum') }}</div>
							<div class="font-bold">{{ formatMoney(payment.rechnungsbetrag) }}</div>
						</div>
						<div class="flex justify-between">
							<div>{{ $t('paid_amount') }}</div>
							<div class="font-bold">{{ formatMoney(payed) }}</div>
						</div>
						<div class="flex justify-between mb-4">
							<div>{{ $t('open_amount_input') }}</div>
							<div class="font-bold" :class="{ 'text-red-500': notPayed < 0 }">{{ formatMoney(notPayed) }}</div>
						</div>
						<div v-if="daysSinceInvoice && notPayed > 0">
							{{ $t('billing_amount_since') }}<span class="font-bold">{{ daysSinceInvoice }}</span> {{ $t('days_open') }}
						</div>
						<div :class="{ 'text-red-500 font-bold': showWarning }" class="flex justify-between mb-4">
							<div class="pr-2">{{ $t('status_input') }}</div>
							<div class="font-bold">{{ statusLabel }}</div>
						</div>
					</div>
				</div>
				<div class="mt-4" v-if="!isUserAllowedInLevel3">
					<div class="font-bold text-left text-xl mb-4 xl:mb-4">{{ $t('notes') }}</div>
					<BaseInput v-model="payment.note">
						<textarea v-model="payment.note"></textarea>
					</BaseInput>
				</div>
			</form>
			<div class="font-bold text-red-300" v-if="payment && payment.date === null && payment.status">
				{{ $t('current_status_input') }} {{ statusOptions[payment.status] }}
			</div>
		</div>
		<div class="flex justify-between bg-white bg-opacity-90 footer pt-8 w-full" v-if="!isUserAllowedInLevel3">
			<div class="flex space-x-4 bg-white bg-opacity-90">
				<BaseButton @click="toggleStatus">
					<font-awesome-icon :icon="['fas', getOpoIcon]" class="w-5 h-5 pr-1" size="lg" />
					{{ getOpoStateTitle }}
				</BaseButton>

				<router-link
					:to="{ name: 'damagecases.dunning', params: { id: $route.params.processId || $route.params.id } }"
					class="btn-default"
					v-if="isUserAllowedInLevel2"
				>
					{{ $t('to_dunning') }}
				</router-link>
			</div>
			<BaseButton @click="save" class="btn-blue">{{ $t('general.save') }}</BaseButton>
		</div>
	</div>
	<LoadingSpinner class="mb-10" v-else />
</template>

<script>
import { PaymentApiFactory } from '@/api/generated';
import defaultConfiguration from '@/api/defaultConfiguration';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker';
import DamageCasePaymentReceiptRow from '@/components/rows/DamageCasePaymentReceiptRow';
import BaseButton from '@/components/forms/buttons/BaseButton';
import { v4 } from 'uuid';
import { getOptionFromOptionsArray, getUnixTimestamp } from '@/plugins/Utils';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber';
import BaseInput from '@/components/forms/inputs/BaseInput';
import Authentication from '@/mixins/Authentication';
import Validate from '@/mixins/Validate';
import Formatter from '@/mixins/Formatter';
import DunningStatus from '@/mixins/damagecase/DunningStatus';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import SystemSettingsRepository from '@/api/repositories/SystemSettingsRepository';
import { parsePaymentDeadlineData } from '@/helpers/Utils';
import DamageCasePaymentRepository from '@/api/repositories/DamageCasePaymentRepository';

const paymentApiFactory = PaymentApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios);

export default {
	name: 'Payment',
	components: { LoadingSpinner, BaseInput, BaseInputNumber, BaseButton, DamageCasePaymentReceiptRow, BaseDatepicker },
	mixins: [Authentication, Validate, Formatter, DunningStatus],
	data() {
		return {
			paymentShadow: null,
			isLoading: false,
			payment: null,
			typeOptions: [
				{
					display: this.$t('gross'),
					value: 'brutto',
				},
				{
					display: this.$t('net'),
					value: 'netto',
				},
				{
					display: this.$t('vat'),
					value: 'mehrwertsteuer',
				},
				{
					display: this.$t('payback_to'),
					value: 'refund',
				},
			],
			payerOptions: [
				{
					display: this.$t('customer'),
					value: 'kunde',
				},
				{
					display: this.$t('insurance'),
					value: 'versicherung',
				},
				{
					display: this.$t('lawyer'),
					value: 'anwalt',
				},
			],
			overpaymentSelectOptions: [
				{ display: this.$t('interest_on_arrears'), value: 'interest_on_arrears' },
				{ display: this.$t('other'), value: 'misc' },
			],
			statusOptions: {
				payment_reminder_customer_without_lawyer: this.$t('payment_reminder_customer_without_lawyer'),
				second_payment_reminder_customer_without_lawyer: this.$t('second_payment_reminder_customer_without_lawyer'),
				abgeschlossen: 'Abgeschlossen',
				payment_reminder_customer_with_lawyer: this.$t('payment_reminder_customer_with_lawyer'),
				second_payment_reminder_customer_with_lawyer: this.$t('second_payment_reminder_customer_with_lawyer'),
				first_dunning_notice: this.$t('first_dunning_notice'),
				last_dunning_notice: this.$t('last_dunning_notice'),
			},
			overpaymentOptions: {
				interest_on_arrears: this.$t('interest_on_arrears'),
				misc: this.$t('other'),
			},
			apiPaymentDeadline: null,
		};
	},
	created() {
		this.isLoading = true;
		this.init(() => {
			this.getSystemSettings();
			this.isLoading = false;
			this.$store.commit('setConfirmLeaving', () => {
				if (this.isChanged) {
					return this.$t('changes_made_leave');
				}

				return false;
			});
		});
	},
	computed: {
		isChanged() {
			return !(this.isUserAllowedInLevel3 || this.isClosed || this._paymentShadow === JSON.stringify(this.payment));
		},
		isClosed() {
			return this.payment.status === 'abgeschlossen';
		},
		damageCase() {
			return { ...this.$store.getters.getDamageCase };
		},
		daysSinceInvoice() {
			if (!this.payment.date) {
				return 0;
			}

			if (this.payment.date > new Date()) {
				return null;
			}

			const diff = getUnixTimestamp() - getUnixTimestamp(this.payment.date);
			const minutes = diff / 60;
			const hours = minutes / 60;
			const days = hours / 24;
			return Math.floor(days);
		},
		getOpoIcon() {
			if (this.payment) {
				if (this.payment.date != null) {
					if (this.payment.status !== 'abgeschlossen') {
						return 'lock-open';
					} else {
						return 'lock';
					}
				} else {
					return 'sync-alt';
				}
			} else {
				return 'sync-alt';
			}
		},
		getOpoStateTitle() {
			if (this.payment) {
				if (this.payment.date != null) {
					if (this.payment.status !== 'abgeschlossen') {
						return this.$t('close_opo');
					} else {
						return this.$t('edit_opo');
					}
				} else {
					return 'OPOS-Status bearbeiten';
				}
			} else {
				return 'OPOS-Status bearbeiten';
			}
		},
	},
	methods: {
		checkIsOverpayment(payment) {
			return payment.overpaymentType;
		},
		getSystemSettings() {
			new SystemSettingsRepository().getValue().then(response => {
				this.apiPaymentDeadline = response.data.payment_deadline;
			});
		},
		changeDate() {
			if (this.payment.date !== null) {
				// Zahlungsziel ohne Anwalt +30 Tage, mit Anwalt +60 Tage
				//	const zDays = !this.damageCase.lawyerOffice ? 30 : 60;
				const zDays = parsePaymentDeadlineData(this.apiPaymentDeadline);
				this.payment.zahlungsziel = new Date((getUnixTimestamp(this.payment.date) + zDays * 24 * 3600) * 1000);
			}
		},
		checkOverPay(receipt) {
			if (this.notPayed < 0 && receipt.type && receipt.payer && receipt.date && !receipt.overpaymentType) {
				this.$swal({
					title: this.$t('general.attention').toString(),
					text: this.$t('check_overpayment').toString(),
					icon: 'warning',
					input: 'select',
					inputOptions: this.overpaymentOptions,
					cancelButtonText: this.$t('general.cancel').toString(),
					showCancelButton: true,
				}).then(reassignResult => {
					if (reassignResult.isConfirmed) {
						const openMoney = this.payment.rechnungsbetrag - this.payed;
						if (openMoney < receipt.value) {
							this.payment.zahlungen.find(element => element.id === receipt.id).value = receipt.value - Math.abs(openMoney);
							this.$set(this.payment.zahlungen, this.payment.zahlungen.length, {
								id: v4(),
								isNew: true,
								date: new Date(),
								value: Math.abs(openMoney),
								overpaymentType: this.overpaymentSelectOptions.find(element => element.value === reassignResult.value),
							});
						} else {
							this.payment.zahlungen.find(element => element.id === receipt.id).overpaymentType = this.overpaymentSelectOptions.find(
								element => element.value === reassignResult.value,
							);
						}
					}
				});
			}
		},
		init(callback) {
			return this.$store.dispatch('updateDamageCase', { id: this.$route.params.processId || this.$route.params.id }).then(response => {
				new DamageCasePaymentRepository()
					.showPayment(this.$route.params.processId || this.$route.params.id, this.isUserAllowedInLevel3 ? { type: this.$route.params?.type } : null)
					.then(response => {
						//paymentApiFactory.damageCaseDamageCaseIdDamageCasePaymentGet(this.$route.params.processId || this.$route.params.id).then(response => {
						this.payment = response.data.data;
						if (this.payment.date !== null && this.payment.zahlungsziel === null) {
							// Zahlungsziel ohne Anwalt +30 Tage, mit Anwalt +60 Tage
							const zDays = !this.damageCase.lawyerOffice ? 30 : 60;
							this.payment.zahlungsziel = this.payment.date + zDays * 24 * 3600;
						}
						this.payment.date = this.payment.date ? new Date(this.payment.date * 1000) : null;
						this.payment.zahlungsziel = this.payment.zahlungsziel ? new Date(this.payment.zahlungsziel * 1000) : null;
						this.payment.zahlungen?.map(zahlung => {
							zahlung.date = new Date(zahlung.date * 1000);
							zahlung.payer = getOptionFromOptionsArray(this.payerOptions, zahlung.payer);
							zahlung.type = getOptionFromOptionsArray(this.typeOptions, zahlung.type);
							zahlung.overpaymentType = this.overpaymentSelectOptions.find(element => element.value === zahlung.overpaymentType);
						});

						this._paymentShadow = JSON.stringify(this.payment);
						callback ? callback() : null;
					});
			});
		},
		toggleStatus() {
			if (this.payment.date != null) {
				this.$swal({
					title: this.$t('sure').toString(),
					text: !this.isClosed ? this.$t('set_opo_status_closed').toString() : this.$t('set_opo_status_open').toString(),
					icon: 'warning',
					cancelButtonText: this.$t('general.no').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('general.yes').toString(),
				}).then(result => {
					if (result.value) {
						if (!this.validate()) {
							this.$swal(this.$t('general.error').toString(), this.$t('please_check_input').toString(), 'error');
							return false;
						}

						const isClosed = this.isClosed;
						this.payment.status = isClosed ? null : 'abgeschlossen';
						this.save(!isClosed);
					}
				});
			} else {
				this.$swal({
					title: this.$t('sure').toString(),
					text: this.$t('opo_status_hint').toString(),
					icon: 'warning',
					input: 'select',
					inputOptions: this.statusOptions,
					inputValue: this.payment.status ? this.payment.status : '',
					cancelButtonText: this.$t('general.cancel').toString(),
					showCancelButton: true,
				}).then(reassignResult => {
					if (reassignResult.isConfirmed) {
						if (reassignResult.value) {
							this.payment.status = reassignResult.value;
							this.save(false, false);
						}
					}
				});
			}
		},
		save(updatePayments = true, doValidation = true) {
			if (doValidation) {
				if (!this.validate()) {
					return this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				}
			}
			this.isLoading = true;

			paymentApiFactory
				.damageCasePaymentDamageCasePaymentIdPut(this.payment.id, {
					...this.payment,
					zahlungen: undefined,
					date: this.payment.date ? getUnixTimestamp(this.payment.date) : null,
					zahlungsziel: this.payment.zahlungsziel ? getUnixTimestamp(this.payment.zahlungsziel) : null,
				})
				.then(response => {
					const promises = [];
					this._paymentShadow = JSON.stringify(this.payment);
					if (!updatePayments) {
						this.init();
						this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
						this.$store.dispatch('forceUpdateDamageCase');
						this.isLoading = false;
						return;
					}

					const allZahlungen = this.payment.zahlungen.map(zahlung => {
						return {
							id: zahlung.id,
							isNew: zahlung.isNew,
							date: getUnixTimestamp(zahlung.date),
							payer: zahlung.payer.value,
							type: zahlung.type.value,
							value: zahlung.value,
							overpayment_type: zahlung.overpaymentType ? zahlung.overpaymentType.value : null,
							mwst: this.getUser.config['mwst_full'],
						};
					});

					const newZahlungen = allZahlungen.filter(zahlung => zahlung.isNew);
					const oldZahlungen = allZahlungen.filter(zahlung => !zahlung.isNew);

					if (newZahlungen?.length) {
						promises.push(paymentApiFactory.damageCasePaymentDamageCasePaymentReceiptsBatchPost(this.payment.id, newZahlungen));
					}

					if (oldZahlungen?.length) {
						promises.push(paymentApiFactory.damageCasePaymentDamageCasePaymentReceiptsBatchPut(this.payment.id, oldZahlungen));
					}

					Promise.all(promises)
						.then(response => {
							this.isLoading = false;
							this.init();
							this.$store.dispatch('forceUpdateDamageCase');
							this.$store.commit('setConfirmLeaving', false);
							this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success').then(() => {
								this.$router.push({
									name: 'management.process.edit',
									params: { processId: this.$store.getters.getDamageCase.id, customer: this.$store.getters.getDamageCase.customer.id },
								});
							});
						})
						.catch(error => {
							this.isLoading = false;
							this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
						});
				});
		},
		addPaymentReceipt() {
			this.$set(this.payment.zahlungen, this.payment.zahlungen.length, {
				id: v4(),
				isNew: true,
				date: new Date(),
				overpaymentType: this.notPayed <= 0 ? this.overpaymentSelectOptions[0] : null,
			});
		},
	},
};
</script>

<style>
.min-w-0 {
	min-width: 0 !important;
}
</style>
